import { Link, useNavigate } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard,
  Library,
  ListChecks,
  LogOut,
  Shield,
  Sparkles,
} from 'lucide-react'
import { useMe } from '../lib/me-context'

/**
 * Persistent app chrome for every authenticated host surface.
 *
 * Rendered once by the `_app` layout (above `<Outlet />`) so a host always
 * has the same orientation regardless of which authoring screen they're on:
 *
 *   - a brand wordmark on the leading edge that routes back to the public
 *     home page (`/`) — the only "escape hatch" out of the app shell, which
 *     was previously missing entirely; and
 *   - primary nav links (Dashboard, Quizzes) that surface where the host can
 *     go and highlight where they currently are.
 *
 * Uses TanStack Router `<Link>` so navigation stays client-side and the
 * active route is resolved against the generated route tree (the `[&.active]`
 * Tailwind variant keys off the `active` class Router adds to the matching
 * link). All spacing is logical (`ps-*`, `pe-*`, `gap-*`) so the bar mirrors
 * cleanly under `dir="rtl"`.
 */
export function AppHeader() {
  const { t } = useTranslation('common')
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { isAdmin } = useMe()

  // Sign out from any authenticated surface. On success we drop the shared
  // `['auth', 'me']` cache (so the `_app` gate sees an anonymous session)
  // and route to the public home page — fully client-side.
  const logoutMutation = useMutation({
    mutationFn: async () => {
      await fetch('/api/v1/auth/sign-out', {
        method: 'POST',
        credentials: 'include',
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
      void navigate({ to: '/' })
    },
  })

  return (
    <header className="sticky top-0 z-40 border-b border-brand-100 bg-white/85 backdrop-blur-md">
      <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          aria-label={t('goToHome')}
          className="inline-flex items-center gap-2 rounded-full px-2 py-1 -ms-2 text-brand-900 transition-colors hover:text-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
        >
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-bl from-brand-500 to-brand-600 text-white shadow-sm">
            <Sparkles className="h-4 w-4" aria-hidden="true" />
          </span>
          <span className="font-display text-lg font-bold tracking-tight" dir="ltr">
            ArabQuiz
          </span>
        </Link>

        <nav aria-label={t('mainNav')}>
          <ul className="flex items-center gap-1 sm:gap-2">
            <li>
              <NavLink to="/dashboard" icon={<LayoutDashboard className="h-4 w-4" aria-hidden="true" />}>
                {t('dashboard')}
              </NavLink>
            </li>
            <li>
              <NavLink to="/quizzes" icon={<ListChecks className="h-4 w-4" aria-hidden="true" />}>
                {t('quizzes')}
              </NavLink>
            </li>
            <li>
              <NavLink to="/question-bank" icon={<Library className="h-4 w-4" aria-hidden="true" />}>
                {t('questionBank')}
              </NavLink>
            </li>
            {isAdmin ? (
              <li>
                <NavLink to="/admin" icon={<Shield className="h-4 w-4" aria-hidden="true" />}>
                  {t('admin')}
                </NavLink>
              </li>
            ) : null}
            <li>
              <button
                type="button"
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
                className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold text-brand-700 transition-colors hover:bg-red-50 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
                <span className="hidden sm:inline">{t('logout')}</span>
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  )
}

type NavLinkProps = {
  to: '/dashboard' | '/quizzes' | '/question-bank' | '/admin'
  icon: React.ReactNode
  children: React.ReactNode
}

/**
 * A single primary nav entry. The label is hidden on the narrowest screens
 * (icon-only) so the bar never overflows, and the active route is rendered
 * with a filled brand pill via Router's `active` class + the `[&.active]`
 * Tailwind variant.
 */
function NavLink({ to, icon, children }: NavLinkProps) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold text-brand-700 transition-colors hover:bg-brand-50 hover:text-brand-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 [&.active]:bg-brand-500 [&.active]:text-white [&.active]:hover:bg-brand-600 [&.active]:hover:text-white"
    >
      {icon}
      <span className="hidden sm:inline">{children}</span>
    </Link>
  )
}
